import { useState } from 'react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription } from '@/components/ui/dialog';
import { Plus, Minus, DollarSign, Search } from 'lucide-react';
import ProfilePicture from '@/components/ProfilePicture';

// Assign Contributor Modal
export const AssignContributorModal = ({ 
  open, 
  onOpenChange, 
  allUsers, 
  contributors, 
  onAssign 
}) => {
  const [searchTerm, setSearchTerm] = useState('');
  
  const filteredUsers = allUsers.filter(u => 
    !contributors.some(c => c.user_id === u.id) &&
    (u.full_name?.toLowerCase().includes(searchTerm.toLowerCase()) ||
     u.email?.toLowerCase().includes(searchTerm.toLowerCase()))
  );
  
  const handleAssign = async (userId) => {
    const success = await onAssign(userId);
    if (success) {
      onOpenChange(false);
      setSearchTerm('');
    }
  };
  
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-md">
        <DialogHeader>
          <DialogTitle>Assign Contributor Role</DialogTitle>
        </DialogHeader>
        <div className="space-y-4">
          <div className="relative">
            <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 w-4 h-4 text-gray-400" />
            <Input
              placeholder="Search users..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="pl-10"
            />
          </div>
          <div className="max-h-64 overflow-y-auto space-y-2">
            {filteredUsers.slice(0, 20).map(user => (
              <div 
                key={user.id}
                className="flex items-center justify-between p-3 bg-slate-100 rounded-lg hover:bg-slate-200 cursor-pointer"
                onClick={() => handleAssign(user.id)}
              >
                <div className="flex items-center gap-3">
                  <ProfilePicture src={user.profile_picture} alt={user.full_name} size="sm" />
                  <div>
                    <p className="font-medium">{user.full_name}</p>
                    <p className="text-sm text-gray-500">{user.email}</p>
                  </div>
                </div>
                <Plus className="w-4 h-4 text-cyan-600" />
              </div>
            ))}
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
};

// Adjust Points Modal
export const AdjustPointsModal = ({ 
  open, 
  onOpenChange, 
  selectedContributor, 
  onAdjust 
}) => {
  const [adjustmentData, setAdjustmentData] = useState({ points: 0, reason: '' });
  
  const handleAdjust = async () => {
    const success = await onAdjust(
      selectedContributor?.user_id,
      adjustmentData.points,
      adjustmentData.reason
    );
    if (success) {
      onOpenChange(false);
      setAdjustmentData({ points: 0, reason: '' });
    }
  };
  
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>Adjust Points for {selectedContributor?.user?.full_name}</DialogTitle>
        </DialogHeader>
        <div className="space-y-4">
          <div>
            <Label>Points Adjustment</Label>
            <div className="flex items-center gap-2 mt-1">
              <Button
                variant="outline"
                size="sm"
                onClick={() => setAdjustmentData(d => ({ ...d, points: d.points - 1 }))}
              >
                <Minus className="w-4 h-4" />
              </Button>
              <Input
                type="number"
                value={adjustmentData.points}
                onChange={(e) => setAdjustmentData(d => ({ ...d, points: parseInt(e.target.value) || 0 }))}
                className="text-center w-24"
              />
              <Button
                variant="outline"
                size="sm"
                onClick={() => setAdjustmentData(d => ({ ...d, points: d.points + 1 }))}
              >
                <Plus className="w-4 h-4" />
              </Button>
            </div>
            <p className="text-xs text-gray-500 mt-1">Use negative values to deduct points</p>
          </div>
          
          <div>
            <Label>Reason *</Label>
            <Textarea
              value={adjustmentData.reason}
              onChange={(e) => setAdjustmentData(d => ({ ...d, reason: e.target.value }))}
              placeholder="Explain the reason for this adjustment..."
              className="mt-1"
            />
          </div>
          
          <div className="flex justify-end gap-2">
            <Button variant="outline" onClick={() => onOpenChange(false)}>
              Cancel
            </Button>
            <Button 
              onClick={handleAdjust}
              disabled={!adjustmentData.reason || adjustmentData.points === 0}
              className="bg-cyan-600 hover:bg-cyan-700"
            >
              Apply Adjustment
            </Button>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
};

// Manual Revenue Modal
export const ManualRevenueModal = ({ 
  open, 
  onOpenChange, 
  settings, 
  onSave 
}) => {
  const [manualRevenueData, setManualRevenueData] = useState({ amount: '', fees: '', notes: '' });
  
  const handleSave = async () => {
    const success = await onSave(
      manualRevenueData.amount,
      manualRevenueData.fees,
      manualRevenueData.notes
    );
    if (success) {
      onOpenChange(false);
      setManualRevenueData({ amount: '', fees: '', notes: '' });
    }
  };
  
  const netRevenue = (parseFloat(manualRevenueData.amount) || 0) - (parseFloat(manualRevenueData.fees) || 0);
  const poolAmount = netRevenue * (settings.revenue_share_percentage / 100);
  
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>Manual Revenue Entry</DialogTitle>
          <DialogDescription>
            Enter revenue manually when automatic calculation shows $0 or needs adjustment.
            This is useful when revenue was collected outside the system.
          </DialogDescription>
        </DialogHeader>
        <div className="space-y-4 mt-4">
          <div>
            <Label>Total Membership Revenue *</Label>
            <div className="relative mt-1">
              <DollarSign className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 w-4 h-4" />
              <Input
                type="number"
                step="0.01"
                min="0"
                placeholder="0.00"
                value={manualRevenueData.amount}
                onChange={(e) => setManualRevenueData({ ...manualRevenueData, amount: e.target.value })}
                className="pl-10"
              />
            </div>
            <p className="text-xs text-gray-500 mt-1">
              Total revenue collected from memberships this month
            </p>
          </div>
          
          <div>
            <Label>Processing Fees (Optional)</Label>
            <div className="relative mt-1">
              <DollarSign className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 w-4 h-4" />
              <Input
                type="number"
                step="0.01"
                min="0"
                placeholder="0.00"
                value={manualRevenueData.fees}
                onChange={(e) => setManualRevenueData({ ...manualRevenueData, fees: e.target.value })}
                className="pl-10"
              />
            </div>
            <p className="text-xs text-gray-500 mt-1">
              Deducted before calculating contribution pool
            </p>
          </div>
          
          <div>
            <Label>Notes (Optional)</Label>
            <Textarea
              placeholder="Reason for manual entry, source of revenue, etc."
              value={manualRevenueData.notes}
              onChange={(e) => setManualRevenueData({ ...manualRevenueData, notes: e.target.value })}
              rows={2}
              className="mt-1"
            />
          </div>
          
          {manualRevenueData.amount && (
            <div className="p-3 bg-slate-800 rounded-lg">
              <p className="text-sm text-gray-400">Contribution Pool Preview:</p>
              <p className="text-xl font-bold text-cyan-400">
                ${poolAmount.toFixed(2)}
              </p>
              <p className="text-xs text-gray-500">
                ({settings.revenue_share_percentage}% of ${netRevenue.toFixed(2)} net revenue)
              </p>
            </div>
          )}
        </div>
        <DialogFooter className="mt-4">
          <Button variant="outline" onClick={() => onOpenChange(false)}>
            Cancel
          </Button>
          <Button onClick={handleSave} className="bg-cyan-600 hover:bg-cyan-700">
            Save Revenue
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};
